import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, Sparkles } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

interface WaitlistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const WaitlistModal = ({ open, onOpenChange }: WaitlistModalProps) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    if (!gdprConsent) {
      toast({
        title: "Consent required",
        description: "You must agree to the processing of your data to continue.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("join-waitlist", {
        body: { email: trimmed },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      onOpenChange(false);
      setEmail("");
      navigate("/waitlist");
    } catch (err: any) {
      toast({
        title: "Something went wrong",
        description: err.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (value: boolean) => {
    if (!loading) {
      onOpenChange(value);
      if (!value) setEmail("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <DialogTitle className="text-center">Join the Waitlist</DialogTitle>
          <DialogDescription className="text-center">
            Enter your email to get early access to unlimited scans, priority support, and advanced features.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            className="h-12"
            aria-label="Email address"
          />
          <div className="flex items-start space-x-2">
            <Checkbox
              id="gdpr-waitlist"
              checked={gdprConsent}
              onCheckedChange={(checked) => setGdprConsent(checked === true)}
              disabled={loading}
            />
            <label htmlFor="gdpr-waitlist" className="text-xs text-muted-foreground leading-tight cursor-pointer">
              I agree to the processing of my data in accordance with GDPR
            </label>
          </div>
          <Button type="submit" className="w-full h-12 font-semibold" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Joining…
              </>
            ) : (
              <>
                Join the Waitlist
                <ArrowRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistModal;
